// Client API du catalogue des modèles d'appareils (éditeur d'architecture).
// Deux niveaux : la liste commune (super admin) et la personnalisation de chaque organisation
// (org admin : renommer / masquer un modèle commun, ajouter ses propres modèles).
// Le numéro de série n'en fait pas partie : il est affecté à chaque appareil du schéma.
import { translate } from '../i18n';

const BASE_URL = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

export type DeviceModelCategory = 'charger' | 'socket' | 'other';

export interface DeviceModel {
  id: number;
  /** null : modèle commun ; sinon l'organisation propriétaire. */
  orgId: number | null;
  scope: 'common' | 'org';
  /** Identifiant stable enregistré dans les schémas (zone_equipment.type), ex. « wattelgo ». */
  code: string;
  name: string;
  nameEn: string | null;
  nameHi: string | null;
  icon: string | null;
  imageUrl: string | null;
  category: DeviceModelCategory;
  sortOrder: number;
  active: boolean;
  /** Anciens noms, encore reconnus dans les schémas existants. */
  formerNames: string[];
  /** Modèle commun masqué par l'organisation. */
  hidden: boolean;
  /** Modèle commun renommé ou masqué par l'organisation. */
  customized: boolean;
  /** Nom commun d'un modèle personnalisé. */
  baseName: string | null;
}

export type DeviceModelInput = Partial<Pick<DeviceModel, 'code' | 'name' | 'nameEn' | 'nameHi' | 'icon' | 'imageUrl' | 'category' | 'sortOrder' | 'active'>>;
export type DeviceModelOverride = Partial<Pick<DeviceModel, 'name' | 'nameEn' | 'nameHi' | 'hidden'>>;

function headers(): HeadersInit {
  const token = localStorage.getItem('dashboard_zce_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

/** Mode support (installateur agissant pour une organisation cliente), comme api/architecture.ts. */
export function actingOrgId(): string | null {
  return sessionStorage.getItem('actingClientOrgId');
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers: headers() });
  const body = await res.json().catch(() => ({}));
  // seuls les messages métier (chaîne) sont affichés ; une erreur serveur retombe sur le message générique
  const errMsg = typeof body.error === 'string' ? body.error : undefined;
  if (!res.ok || body.success === false) throw Object.assign(new Error(errMsg || translate('adm_err_status', { status: res.status })), { status: res.status });
  return body as T;
}

const query = (params: Record<string, string | null | undefined>) => {
  const q = Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v!)}`).join('&');
  return q ? `?${q}` : '';
};

export const deviceModelsApi = {
  /** Catalogue de l'organisation active (ou de l'organisation en mode support) ; `all` : y compris masqués et désactivés. */
  async list(all = false): Promise<DeviceModel[]> {
    return (await call<{ models: DeviceModel[] }>(`/api/device-models${query({ all: all ? '1' : null, orgId: actingOrgId() })}`)).models;
  },

  // Liste commune — super admin
  async create(data: DeviceModelInput): Promise<DeviceModel> {
    return (await call<{ model: DeviceModel }>('/api/admin/device-models', { method: 'POST', body: JSON.stringify(data) })).model;
  },
  async update(id: number, data: DeviceModelInput): Promise<DeviceModel> {
    return (await call<{ model: DeviceModel }>(`/api/admin/device-models/${id}`, { method: 'PUT', body: JSON.stringify(data) })).model;
  },
  async remove(id: number): Promise<void> {
    await call(`/api/admin/device-models/${id}`, { method: 'DELETE' });
  },

  // Personnalisation de l'organisation — org admin
  async createForOrg(data: DeviceModelInput): Promise<DeviceModel> {
    return (await call<{ model: DeviceModel }>('/api/org/device-models', { method: 'POST', body: JSON.stringify(data) })).model;
  },
  /** Modèle propre : modification ; modèle commun : renommage / masquage pour l'organisation. */
  async updateForOrg(id: number, data: DeviceModelInput | DeviceModelOverride): Promise<DeviceModel> {
    return (await call<{ model: DeviceModel }>(`/api/org/device-models/${id}`, { method: 'PUT', body: JSON.stringify(data) })).model;
  },
  /** Modèle propre : suppression ; modèle commun : retour au nom commun. */
  async removeForOrg(id: number): Promise<void> {
    await call(`/api/org/device-models/${id}`, { method: 'DELETE' });
  },
};
